import { useEffect } from 'react';
import { useRecurly } from '@recurly/react-recurly';

import { isAnnualBillingFrequency } from '../../../helpers/flex-helpers/flexBillingFrequency';
import { FamilyName, PaymentMethodTypes, TrialDuration } from '../constants.ts';

/**
 * Custom hook to handle Apple Pay integration with Recurly.
 *
 * @param {HTMLElement} applePayButton - The Apple Pay button element.
 * @param {Object} billingDetails - The billing details for the payment.
 * @param {boolean} billingDetails.isMethodChange - Indicates if the payment method is being changed.
 * @param {Object} billingDetails.upgradeOffer - The upgrade offer details.
 * @param {boolean} billingDetails.isTrial - Indicates if the payment is for a trial period.
 * @param {string} billingDetails.price - The price of the subscription.
 * @param {string} billingDetails.proRataPrice - The pro-rata price for the subscription.
 * @param {boolean} billingDetails.isProRataMovement - Indicates if the payment is a pro-rata movement.
 * @param {moment.Moment} billingDetails.billingStartDate - The start date of the billing period.
 * @param {moment.Moment} billingDetails.nextBillingDate - The next billing date.
 * @param {moment.Moment} billingDetails.remainingTimeUntilDate - The remaining time until the next billing date.
 * @param {Function} onSuccess - Callback function to be called on successful payment authorization.
 * @param {Function} onError - Callback function to be called on payment authorization error.
 *
 * @returns {Object} The Apple Pay configuration object.
 */

const useApplePay = (applePayButton, billingDetails, onSuccess, onError) => {
  const {
    isMethodChange,
    isBillingDateChange,
    changeBillingDatePaymentValue,
    upgradeOffer,
    isTrial,
    price,
    billingFrequency,
    proRataPrice,
    isProRataMovement,
    billingStartDate,
    nextBillingDate,
    remainingTimeUntilDate,
  } = billingDetails;

  const recurly = useRecurly();

  const clickAbortController = new AbortController();

  const nextBill = nextBillingDate?.format('DD MMMM YYYY');
  const subscriptionStart = billingStartDate.format('DD MMMM YYYY');
  const delayedStartDate = remainingTimeUntilDate?.format('DD MMMM YYYY');

  const analyticsIdentifier =
    isMethodChange || isBillingDateChange ? PaymentMethodTypes.ApplePay : PaymentMethodTypes.NewApplePay;
  const isApplePayButtonReady = applePayButton;

  const recurringPaymentIntervalUnit = isAnnualBillingFrequency(billingFrequency) ? 'year' : 'month';

  const regularBilling = {
    label: FamilyName[upgradeOffer.family],
    amount: price,
    paymentTiming: 'recurring',
    recurringPaymentIntervalUnit,
    recurringPaymentIntervalCount: 1,
    recurringPaymentStartDate: subscriptionStart,
  };

  // Recurly turns options.recurring into a total with no interval, and Apple Pay
  // displays that total as "per month". The total line has to carry the interval.
  const subscriptionTotal = {
    label: 'Experian',
    amount: price,
    paymentTiming: 'recurring',
    recurringPaymentIntervalUnit,
    recurringPaymentIntervalCount: 1,
    recurringPaymentStartDate: subscriptionStart,
  };

  const trialBilling = {
    label: `Free ${TrialDuration} Day Trial`,
    amount: '0.00',
    paymentTiming: 'recurring',
    recurringPaymentIntervalUnit: 'day',
    recurringPaymentIntervalCount: TrialDuration,
    recurringPaymentEndDate: subscriptionStart,
    ...(delayedStartDate && { recurringPaymentStartDate: delayedStartDate }),
  };

  const proRata = {
    label: 'Today’s payment',
    amount: proRataPrice,
    paymentTiming: 'immediate',
    type: 'final',
  };

  const methodChangeBilling = {
    label: FamilyName[upgradeOffer.family],
    amount: price,
    paymentTiming: 'recurring',
    recurringPaymentStartDate: nextBill,
  };

  const paymentDescription = FamilyName[upgradeOffer.family];

  const buildChangeBillDateRequest = () => ({
    total: {
      label: 'Experian',
      amount: changeBillingDatePaymentValue,
    },
    lineItems: [
      {
        label: 'One-time payment',
        amount: changeBillingDatePaymentValue,
        paymentTiming: 'immediate',
        type: 'final',
      },
    ],
  });

  const buildMethodChangeRequest = () => ({
    total: {
      label: 'Experian',
      amount: price,
    },
    recurringPaymentRequest: {
      paymentDescription,
      regularBilling: methodChangeBilling,
    },
  });

  const buildSubscriptionRequest = () => ({
    ...(!isTrial && { total: subscriptionTotal }),
    recurringPaymentRequest: {
      paymentDescription,
      regularBilling,
      ...(isTrial && { trialBilling }),
    },
    ...(isProRataMovement && {
      lineItems: [proRata],
    }),
  });

  const getPaymentRequest = () => {
    if (isBillingDateChange) return buildChangeBillDateRequest();
    if (isMethodChange) return buildMethodChangeRequest();
    return buildSubscriptionRequest();
  };

  const getTotal = () => {
    if (isTrial) return 0;
    if (isBillingDateChange) return changeBillingDatePaymentValue;
    return price;
  };

  const paymentRequest = getPaymentRequest();
  const total = getTotal();

  const applePayConfig = {
    country: 'GB',
    currency: 'GBP',
    label: 'Experian',
    total,
    recurring: !isBillingDateChange,
    paymentRequest,
    callbacks: {
      onPaymentAuthorized: ({ payment }) => {
        const { recurlyToken } = payment;
        onSuccess(recurlyToken, analyticsIdentifier);
      },
    },
  };

  useEffect(() => {
    if (!isApplePayButtonReady) return undefined;

    const applePay = recurly.ApplePay(applePayConfig);

    const handleClick = () => {
      applePay.begin();
    };

    applePay.ready(() => {
      applePayButton.addEventListener('click', handleClick, { signal: clickAbortController.signal });
    });

    applePay.on('error', err => {
      onError(err, analyticsIdentifier);
    });

    return () => {
      clickAbortController.abort('applepay_recreate');
    };
  }, [isApplePayButtonReady, applePayButton, applePayConfig]);

  return applePayConfig;
};

export default useApplePay;
